import React from "react";
import { UserWithProfile } from "../../../Entities/User";
import { PaginationType } from "../../../Interface";
import { ColumnsType } from "antd/es/table";
import { Button, Empty, Table, Tag, Typography } from "antd";
import {
  EyeOutlined,
  Loading3QuartersOutlined,
  MailOutlined,
  PhoneOutlined,
  UserOutlined,
} from "@ant-design/icons";
import dayjs from "dayjs";
import { UserProfile } from "../../../Entities/UserProfile";
import { useNavigate } from "react-router-dom";

interface TalentPoolTableProps {
  loading: boolean;
  users: UserWithProfile[];
  pagination: PaginationType;
  updatePagination: (page: number) => void;
}

const formatTotalExperience = (
  experience: UserProfile["experience"] | undefined,
): string => {
  if (!experience?.length) return "Fresher";

  const totalMonths = experience.reduce((sum, exp) => {
    if (!exp.from) return sum;
    const start = dayjs(exp.from);
    const end = exp.to ? dayjs(exp.to) : dayjs();
    return sum + end.diff(start, "month");
  }, 0);

  if (totalMonths <= 0) return "Fresher";

  const years = Math.floor(totalMonths / 12);
  const months = totalMonths % 12;

  if (years === 0) return `${months} month${months !== 1 ? "s" : ""}`;
  if (months === 0) return `${years} year${years !== 1 ? "s" : ""}`;
  return `${years} year${years !== 1 ? "s" : ""} ${months} month${months !== 1 ? "s" : ""}`;
};

const getUserResumeInfo = (userProfile: UserProfile | undefined) => {
  const experience = userProfile?.experience ?? [];
  return {
    role: experience.length > 0 ? experience[0].designation : "NA",
    totalExperience: formatTotalExperience(experience),
  };
};

const TalentPoolTable: React.FC<TalentPoolTableProps> = ({
  loading,
  users,
  pagination,
  updatePagination,
}) => {
  const navigate = useNavigate();

  const columns: ColumnsType<UserWithProfile> = [
    {
      title: "Candidate",
      dataIndex: "candidate",
      key: "candidate",
      align: "start",
      width: 210,
      render: (_, record) => (
        <div>
          <Typography.Text strong>
            <UserOutlined /> {record.firstName} {record.lastName}
          </Typography.Text>
          <br />
          <Typography.Text type="secondary">
            <MailOutlined /> {record.email}
          </Typography.Text>
          <br />
          <Typography.Text type="secondary">
            <PhoneOutlined rotate={90} /> {record?.mobileNumber || "NA"}
          </Typography.Text>
        </div>
      ),
    },
    {
      title: "Recent Role",
      dataIndex: "role",
      key: "role",
      align: "center",
      width: 160,
      render: (_, record) => (
        <Typography.Text className="text-wrap">
          {record.profile ? getUserResumeInfo(record.profile).role : "-"}
        </Typography.Text>
      ),
    },
    {
      title: "Experience",
      dataIndex: "experience",
      key: "experience",
      align: "center",
      width: 160,
      render: (_, record) => {
        if (!record.profile) {
          return <Typography.Text type="secondary">-</Typography.Text>;
        }
        const exp = getUserResumeInfo(record.profile).totalExperience;
        return exp === "NA" ? (
          <Typography.Text type="secondary">{exp}</Typography.Text>
        ) : (
          <Tag color="magenta">{exp}</Tag>
        );
      },
    },
    {
      title: "Top Skills",
      dataIndex: "skills",
      key: "skills",
      align: "center",
      width: 300,
      render: (_, record) => {
        if (!record.profile) {
          return <Typography.Text>-</Typography.Text>;
        }
        const skills = record.profile.skills ?? [];
        const visible = skills.slice(0, 3);
        const remaining = skills.length - visible.length;
        return (
          <div className="table-skills">
            {visible.map((s, i) => (
              <Tag key={i} color="blue" className="text-wrap">
                {s}
              </Tag>
            ))}
            {remaining > 0 && (
              <Typography.Text type="secondary">
                +{remaining} more
              </Typography.Text>
            )}
          </div>
        );
      },
    },
    {
      title: "Action",
      key: "action",
      align: "center",
      width: 100,
      render: (_: unknown, record) => (
        <Button
          type="text"
          icon={<EyeOutlined />}
          onClick={() => navigate(`/candidate/${record._id}`)}
        />
      ),
    },
  ];

  return (
    <Table<UserWithProfile>
      rowKey="_id"
      bordered={false}
      columns={columns}
      dataSource={users}
      rowSelection={{
        type: "checkbox",
        fixed: true,
      }}
      loading={{
        spinning: loading,
        indicator: <Loading3QuartersOutlined spin={loading} />,
        tip: "Please wait...",
      }}
      locale={{
        emptyText: (
          <Empty
            image={Empty.PRESENTED_IMAGE_SIMPLE}
            description="No talent pools found."
          />
        ),
      }}
      pagination={{
        current: pagination.page,
        pageSize: pagination.limit,
        total: pagination.totalRecords,
        showSizeChanger: false,
        onChange: updatePagination,
        showTotal: (total, range) =>
          `Showing ${range[0]}–${range[1]} of ${total} talent pool${total !== 1 ? "s" : ""}`,
      }}
      scroll={{ y: "calc(100vh - 450px)", x: 930 }}
    />
  );
};

export default TalentPoolTable;
